import Media from "@/classes/Media";
import axios from "axios";
import sharedMethods from "../../sharedMethods.js";
import { ActionTree } from "vuex";
import { GlobalInterface } from "../index.js";
import { StateInterface } from "./state";
import ContentGroup from "@/classes/ContentGroup";

const actions: ActionTree<StateInterface, GlobalInterface> = {
	/**
	 * fetch all media files from server
	 * @returns response
	 * @see https://api-dev.mirrads.de/api-docs/#/Mediamanagement/post_api_media
	 */
	async fetchMediaFiles({ commit, rootState }) {
		const plinfo = {
			accID: rootState.user.AccID,
		};

		const response = await axios.post(rootState.api + "media", plinfo);
		const files: (ContentGroup | Media)[] = [];

		response.data.forEach((file: any) => {
			if (file.contentGroupId) {
				files.push(ContentGroup.fromObject(file));
			} else {
				files.push(Media.fromObject(file));
			}
		});

		commit("setMediaFiles", files);
		return response;
	},
	/**
	 * delete media file
	 * @param payload must cotain id
	 * @see https://api-dev.mirrads.de/api-docs/#/Mediamanagement/post_api_delContent
	 * @returns response
	 */
	async deleteMediaFile(
		{ rootState, commit },
		obj: { contentId: string | string[]; groupId?: string }
	) {
		const info = {
			AccID: rootState.user.AccID,
			id: obj.contentId,
		};

		const response = await axios.post(rootState.api + "delContent/", info);

		if (response.status === 200) {
			// we commit after successfull delete to show the user a safe & consistent system

			if (obj.groupId) {
				commit("removeContentGroup", obj.groupId);
			} else {
				if (Array.isArray(obj.contentId)) {
					obj.contentId.forEach((id) => {
						commit("removeMediaFile", id);
					});
				} else {
					commit("removeMediaFile", obj.contentId);
				}
			}
		}

		return response;
	},

	uploadFile(
		{ rootState },
		obj: { file: string | Blob | any; onUploadProgress: any }
	) {
		let formData = new FormData();

		if (obj.file.type === "application/pdf") {
			formData.append("userId", rootState.user.id);
			formData.append("file", obj.file);
			return axios
				.post(rootState.api + "mediamanagement/upload-pdf", formData, {
					headers: {
						"Content-Type": "multipart/form-data",
					},
					onUploadProgress: obj.onUploadProgress,
				})
				.then((result) => {
					sharedMethods.successNotify("File uploaded.");
					return result;
				});
		} else {
			formData.append("accid", rootState.user.AccID.toString());
			formData.append("file", obj.file);
			formData.append("uploadedBy", rootState.user.username);
			return axios
				.post(rootState.api + "upload", formData, {
					headers: {
						"Content-Type": "multipart/form-data",
					},
					onUploadProgress: obj.onUploadProgress,
				})
				.then((result) => {
					sharedMethods.successNotify("File uploaded.");
					return result;
				});
		}
	},

	/**
	 * Adds a youtube video to an account
	 * @param payload name, src
	 * @see https://api-dev.mirrads.de/api-docs/#/Mediamanagement/post_api_mediamanagement_webview_youtube
	 */
	async addYoutubeContent({ dispatch, rootState }, payload) {
		const loading = sharedMethods.loadingNotify("Creating content...");

		const info = {
			accId: rootState.user.AccID,
			uploadedBy: rootState.user.username,
			name: payload.contentName,
			src: payload.src,
		};

		return await axios
			.post(rootState.api + "mediamanagement/content-youtube", info)
			.then(() => {
				// we need to refetch here, because thumbnails are created server side
				loading();
				sharedMethods.successNotify("Youtube video added.");
				dispatch("fetchMediaFiles");
			});
	},

	/**
	 * adds a non media content ! no instagram content !
	 * @param payload name, src, mimetype & duration
	 * @see addInstagramContent for adding a instagram content
	 * @see https://api-dev.mirrads.de/api-docs/#/Mediamanagement/post_api_mediamanagement_content
	 */
	async addNonMediaContent({ dispatch, rootState }, payload) {
		const loading = sharedMethods.loadingNotify("Creating content...");

		const info = {
			accid: rootState.user.AccID,
			uploadedBy: rootState.user.username,
			name: payload.contentName,
			src: payload.src,
			mimetype: payload.mimetype,
			duration: payload.duration,
		};

		return await axios
			.post(rootState.api + "mediamanagement/content", info)
			.then(() => {
				// we need to refetch here, because thumbnails are created server side
				loading();
				sharedMethods.successNotify("Content added.");
				dispatch("fetchMediaFiles");
			});
	},

	/**
	 * rotates a content
	 * @param contentId id of content to rotate
	 * @param orientation:
	 * @returns
	 */
	async rotateContent(
		{ commit, dispatch, rootState },
		data: { contentId: string; orientation: string }
	) {
		commit("setMediaFilesWhileContentRotation", data.contentId);
		return new Promise((resolve, reject) => {
			axios
				.post(rootState.api + "mediamanagement/rotate-video/", data)
				.then((response) => {
					dispatch("fetchMediaFiles");
					resolve(response);
				})
				.catch((err) => {
					return reject(err);
				});
		});
	},

	/**
	 * rotates a content
	 * @param contentId id of content to rotate
	 * @param orientation:
	 * @returns
	 */
	async flipContent({ dispatch, rootState }, data: { contentId: string }) {
		return new Promise((resolve, reject) => {
			axios
				.post(rootState.api + "mediamanagement/flip-video/", data)
				.then((response) => {
					dispatch("fetchMediaFiles");
					resolve(response);
				})
				.catch((err) => {
					return reject(err);
				});
		});
	},

	/**
	 * updates content information such as duration, name or src
	 * @param contentId id of content to rotate
	 * @param duration display duration as string in seconds
	 * @param name new name
	 * @param src new src
	 */
	async updateContentInfo(
		{ commit, dispatch, rootState },
		data: {
			contentId: string;
			name?: string;
			src?: string;
			duration?: string;
			productUrl?: string;
		}
	) {
		commit("updateContentInfo", data);
		return new Promise((resolve, reject) => {
			axios
				.put(rootState.api + "mediamanagement/content", data)
				.then((response) => {
					dispatch("fetchMediaFiles");
					resolve(response);
				})
				.catch((err) => {
					console.log(err);
					reject(err);
				});
		});
	},

	/**
	 * updates the content group name of given contentGroupId
	 * @param contentGroupId id of content to rotate
	 * @param name new name
	 */
	async updateContentGroupName(
		{ commit, dispatch, rootState },
		data: { contentGroupId: string; name: string }
	) {
		commit("updateContentGroupName", data);
		return new Promise((resolve, reject) => {
			axios
				.put(rootState.api + "mediamanagement/content-group", data)
				.then((response) => {
					dispatch("fetchMediaFiles");
					resolve(response);
				})
				.catch((err) => {
					console.log(err);
					reject(err);
				});
		});
	},

	/**
	 * adds a instagram content
	 * @param payload name, numberOfPosts, duration
	 * @see addNonMediaContent for adding a other non media content
	 * @see https://api-dev.mirrads.de/api-docs/#/Mediamanagement/post_api_mediamanagement_content_instagram
	 */
	async addInstagramContent({ dispatch, rootState }, payload) {
		const info = {
			accid: rootState.user.AccID,
			numberOfPosts: payload.numberOfPosts,
			duration: payload.duration,
			uploadedBy: rootState.user.username,
			isAutoUpdate: payload.isAutoUpdate,
			name: payload.name,
		};

		sharedMethods.successNotify("Creating content...");

		return await axios
			.post(rootState.api + "mediamanagement/content-instagram", info)
			.then((response) => {
				if (
					response.data ===
					"Invalid or expired instagram access permission. Please grant permission to proceed."
				) {
					sharedMethods.warnNotify(response.data);
					return false;
				}

				// we need to refetch here because instagram content is fetched from server
				dispatch("fetchMediaFiles");
				return true;
			});
	},
};
export default actions;
